import { useEffect, useMemo } from "react";
import { Tabs } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import {
  buildStructureModalTabConfigs,
  type StructureModalTabId,
} from "./admin-structure-modal-tabs.config";
import { useAdminStructureGeneralDataFormContext } from "./useAdminStructureGeneralDataFormContext";
import { AdminStructureGeneralDataFieldsGate } from "./AdminStructureGeneralDataTab";
import AdminStructureGeometryTab from "./AdminStructureGeometryTab";
import AdminStructureHydrologyTab from "./AdminStructureHydrologyTab";
import AdminStructureRiskMappingTab from "./AdminStructureRiskMappingTab";
import AdminStructureRaisingHistoryTab from "./AdminStructureRaisingHistoryTab";
import AdminStructureReservoirManagementTab from "./AdminStructureReservoirManagementTab";
import AdminStructureAnmClassificationTab from "./AdminStructureAnmClassificationTab";
import AdminStructureRegionalClassificationTab from "./AdminStructureRegionalClassificationTab";

export type AdminStructureModalTabsProps = {
  onActiveTabChange?: (tabId: StructureModalTabId) => void;
  onTabSavingChange?: (isSaving: boolean) => void;
};

export const AdminStructureModalTabs = ({
  onActiveTabChange,
  onTabSavingChange,
}: AdminStructureModalTabsProps) => {
  const { translate } = useLocalization();
  const { complexId, structureId, detail, canWrite, form, onSaved } =
    useAdminStructureGeneralDataFormContext();
  const draftType = form.watch("type");
  const typePersisted = Boolean(detail.type);
  const effectiveType = detail.type ?? draftType;

  useEffect(() => {
    onActiveTabChange?.("generalData");
  }, [effectiveType, onActiveTabChange]);

  const tabs = useMemo(() => {
    const tabConfigs = buildStructureModalTabConfigs(
      effectiveType,
      typePersisted,
    );

    return tabConfigs.map((tab) => ({
      value: tab.value,
      label: translate(tab.labelKey),
      disabled: tab.disabled,
      content: (() => {
        if (tab.value === "generalData") {
          return <AdminStructureGeneralDataFieldsGate />;
        }
        if (
          (tab.value === "geometryDam" || tab.value === "geometry") &&
          effectiveType
        ) {
          return (
            <AdminStructureGeometryTab
              complexId={complexId}
              structureId={structureId}
              structureType={effectiveType}
              canWrite={canWrite}
              detail={detail}
              onSaved={onSaved}
              onSavingChange={onTabSavingChange}
            />
          );
        }
        if (tab.value === "hydrology" && effectiveType) {
          return (
            <AdminStructureHydrologyTab
              complexId={complexId}
              structureId={structureId}
              structureType={effectiveType}
              canWrite={canWrite}
              detail={detail}
              onSaved={onSaved}
              onSavingChange={onTabSavingChange}
            />
          );
        }
        if (tab.value === "riskMapping" && effectiveType === "dam") {
          return (
            <AdminStructureRiskMappingTab
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
              detail={detail}
              onSaved={onSaved}
              onSavingChange={onTabSavingChange}
            />
          );
        }
        if (tab.value === "raisingHistory" && effectiveType === "dam") {
          return (
            <AdminStructureRaisingHistoryTab
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
            />
          );
        }
        if (tab.value === "reservoirManagement" && effectiveType === "dam") {
          return (
            <AdminStructureReservoirManagementTab
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
            />
          );
        }
        if (tab.value === "anmClassification" && effectiveType === "dam") {
          return (
            <AdminStructureAnmClassificationTab
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
              onSaved={() => {}}
              onSavingChange={onTabSavingChange}
            />
          );
        }
        if (tab.value === "regionalClassification" && effectiveType === "dam") {
          return (
            <AdminStructureRegionalClassificationTab
              complexId={complexId}
              structureId={structureId}
              canWrite={canWrite}
              onSaved={() => {}}
              onSavingChange={onTabSavingChange}
            />
          );
        }
        return <div className="min-h-[120px]" />;
      })(),
    }));
  }, [
    canWrite,
    complexId,
    detail,
    effectiveType,
    onSaved,
    onTabSavingChange,
    structureId,
    translate,
    typePersisted,
  ]);

  return (
    <div className="admin-structure-modal-tabs w-full min-w-0">
      <Tabs
        key={effectiveType ?? "pending-type"}
        tabs={tabs}
        defaultValue="generalData"
        onChange={(value) => {
          if (value) {
            onActiveTabChange?.(value as StructureModalTabId);
          }
        }}
      />
    </div>
  );
};
